import { useTheme } from "@/contexts/ThemeContext";
import { AURORA_DARK, AURORA_LIGHT, radial } from "../homeTokens";

/**
 * The aurora — three blurred radial blobs drifting on independent cycles,
 * tinted by the account's current scenario. This is the page's only ambient
 * signal: the user reads "something is wrong" from the colour before reading a
 * single word.
 *
 * Light and dark are not the same effect with swapped values. On dark the
 * blobs glow *out of* near-black, so they run dense and saturated. On light
 * they have to behave like tinted daylight — brighter hue, roughly half the
 * alpha, less blur and no saturation boost. Reusing the dark palette on white
 * turns the whole page muddy grey.
 *
 * The vertical mask fades the field out before it reaches the content, and
 * `pointer-events-none` keeps it out of hit-testing. Motion is suppressed
 * under `prefers-reduced-motion` — colour alone still carries the state.
 *
 * `dimmed` pulls the field back for the conversation. On the home the aurora is
 * the loudest thing on screen and that is correct — it is the ambient state
 * signal for a page you scan. During a conversation the job inverts: the text
 * is what has to be read, and saturated colour behind long paragraphs costs
 * legibility for a signal nobody is looking at any more.
 */
export default function AuroraBackground({ scenario = "ok", dimmed = false }) {
  const { resolved } = useTheme();
  const isLight = resolved === "light";

  const palette = isLight ? AURORA_LIGHT : AURORA_DARK;
  const [a1, a2, a3] = palette[scenario] || palette.ok;

  const mask =
    "linear-gradient(180deg,transparent 0,#000 16%,#000 46%,transparent 78%)";

  return (
    <div
      aria-hidden="true"
      // Surfaced on the element so the active state is visible in devtools —
      // the gradients themselves live in inline styles that are awkward to
      // read back, and jsdom drops them entirely.
      data-scenario={scenario}
      data-theme={isLight ? "light" : "dark"}
      data-dimmed={dimmed ? "true" : "false"}
      className={`pointer-events-none absolute -left-[10%] -right-[10%] -top-[220px] h-[1000px] transition-[opacity,filter] duration-[700ms] ease-[cubic-bezier(.25,.8,.35,1)] motion-reduce:transition-none ${
        isLight ? "blur-[90px] saturate-[1.05]" : "blur-[110px] saturate-[1.25]"
      } ${dimmed ? "opacity-25" : isLight ? "opacity-90" : "opacity-100"}`}
      style={{ WebkitMaskImage: mask, maskImage: mask }}
    >
      <div
        className="absolute left-[4%] top-[30%] h-[52%] w-[52%] rounded-full animate-drift1 motion-reduce:animate-none"
        style={{ background: radial(a1) }}
      />
      <div
        className="absolute right-[2%] top-[34%] h-[50%] w-[50%] rounded-full animate-drift2 motion-reduce:animate-none"
        style={{ background: radial(a2) }}
      />
      <div
        className="absolute left-[28%] top-[50%] h-[46%] w-[54%] rounded-full animate-drift3 motion-reduce:animate-none"
        style={{ background: radial(a3) }}
      />
    </div>
  );
}
